import axios, { type AxiosInstance } from 'axios';

import { getStoredAuth } from '@/lib/authStorage';
import { toCamelCase, toSnakeCase } from '@/lib/case';
import { baseUrl } from '@/lib/config';
import { configureUploadRetry } from '@/lib/retry';

import type { AuthResponse } from '@/features/auth/api';

declare module 'axios' {
    interface AxiosRequestConfig {
        sessionRecoveryReplayed?: boolean;
        uploadSessionRefreshToken?: string;
        sessionRecoveryRefreshToken?: string;
        sessionRecoverySignal?: AbortSignal;
    }
}

type RefreshRequest = (refreshToken: string, signal?: AbortSignal) => Promise<AuthResponse>;

interface SessionRecoveryDeps {
    getAccessToken?: () => string;
    getRefreshToken: () => string;
    onRefreshed: (auth: AuthResponse) => void;
    onSessionEnded: () => void;
    requestRefresh?: RefreshRequest;
    endOnRefreshFailure?: boolean;
    signal?: AbortSignal;
}

// A bare client keeps a failed refresh from trying to refresh itself.
const refreshClient = axios.create({ baseURL: baseUrl });
configureUploadRetry(refreshClient);
interface RecoverySession {
    ended: boolean;
    tokenRecoveries: Map<string, Promise<AuthResponse>>;
    pending: Set<Promise<AuthResponse>>;
}

const refreshesInFlight = new Map<string, Promise<AuthResponse>>();
const recoverySessions = new Map<string, RecoverySession>();
// The deadline includes transport retries and their delays.
const refreshDeadlineMs = 30_000;
// Keep delayed response waves without retaining every credential for the tab lifetime.
const retainedSessions = 16;
const retainedWaves = 16;
const sessionEndListeners = new Set<(refreshToken: string) => void>();

export function subscribeSessionExpiry(listener: (refreshToken: string) => void): () => void {
    sessionEndListeners.add(listener);
    return () => { sessionEndListeners.delete(listener); };
}

function getRecoverySession(refreshToken: string): RecoverySession {
    let session = recoverySessions.get(refreshToken);
    if (!session) {
        session = { ended: false, tokenRecoveries: new Map(), pending: new Set() };
        recoverySessions.set(refreshToken, session);
    }
    recoverySessions.delete(refreshToken);
    recoverySessions.set(refreshToken, session);
    trimRecoveries(session);
    return session;
}

function trimRecoveries(protectedSession?: RecoverySession): void {
    for (const [refreshToken, session] of recoverySessions) {
        for (const [authorization, recovery] of session.tokenRecoveries) {
            if (session.tokenRecoveries.size <= retainedWaves) break;
            if (!session.pending.has(recovery)) session.tokenRecoveries.delete(authorization);
        }
        if (recoverySessions.size > retainedSessions && session.pending.size === 0 && session !== protectedSession) {
            recoverySessions.delete(refreshToken);
        }
    }
}

function isCurrentCredential(deps: SessionRecoveryDeps, refreshToken: string, authorization: string): boolean {
    return deps.getRefreshToken() === refreshToken &&
        (!deps.getAccessToken || `Bearer ${deps.getAccessToken()}` === authorization);
}

function endCurrentSession(deps: SessionRecoveryDeps, refreshToken: string, authorization: string): void {
    if (!isCurrentCredential(deps, refreshToken, authorization)) return;
    endSession(getRecoverySession(refreshToken), () => {
        sessionEndListeners.forEach((listener) => listener(refreshToken));
        deps.onSessionEnded();
    });
}

function waitForRecovery<T>(recovery: Promise<T>, signal?: AbortSignal): Promise<T> {
    if (!signal) return recovery;
    return new Promise<T>((resolve, reject) => {
        const abort = () => {
            signal.removeEventListener('abort', abort);
            reject(new axios.CanceledError());
        };
        if (signal.aborted) { abort(); return; }
        signal.addEventListener('abort', abort, { once: true });
        recovery.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    });
}

export async function refreshAuthSession(
    deps: SessionRecoveryDeps,
    refreshToken = deps.getRefreshToken(),
    authorization = `Bearer ${deps.getAccessToken?.() ?? ''}`,
): Promise<AuthResponse> {
    deps.signal?.throwIfAborted();
    const session = getRecoverySession(refreshToken);
    if (!refreshToken || session.ended || deps.getRefreshToken() !== refreshToken) {
        throw new Error('Session changed');
    }
    let recovery = session.tokenRecoveries.get(authorization);
    if (!recovery) {
        recovery = refreshSession(refreshToken, () => {}, deps.requestRefresh);
        session.pending.add(recovery);
        session.tokenRecoveries.set(authorization, recovery);
        const pendingRecovery = recovery;
        void recovery.catch((error: unknown) => {
            if (!(axios.isAxiosError(error) && error.response?.status === 401) &&
                session.tokenRecoveries.get(authorization) === pendingRecovery) {
                session.tokenRecoveries.delete(authorization);
            }
        }).finally(() => {
            session.pending.delete(pendingRecovery);
            trimRecoveries();
        });
    }
    let auth: AuthResponse;
    try {
        auth = await waitForRecovery(recovery, deps.signal);
    } catch (error) {
        if (deps.signal?.aborted) throw error;
        if (deps.endOnRefreshFailure || (axios.isAxiosError(error) && error.response?.status === 401)) {
            endCurrentSession(deps, refreshToken, authorization);
        } else if (!session.ended && session.tokenRecoveries.get(authorization) === recovery) {
            session.tokenRecoveries.delete(authorization);
        }
        throw error;
    }
    deps.signal?.throwIfAborted();
    if (session.ended || deps.getRefreshToken() !== refreshToken) throw new Error('Session changed');
    if (isCurrentCredential(deps, refreshToken, authorization)) deps.onRefreshed(auth);
    return auth;
}

export function attachSessionExpiry(instance: AxiosInstance, deps: SessionRecoveryDeps): void {
    instance.interceptors.request.use((config) => {
        assertUploadSession(config.uploadSessionRefreshToken, deps.getRefreshToken());
        config.sessionRecoveryRefreshToken = deps.getRefreshToken();
        return config;
    });
    instance.interceptors.response.use((response) => response, async (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            const config = error.config;
            const recoverySignal = config?.sessionRecoverySignal ?? config?.signal;
            if (config && !recoverySignal?.aborted) {
                const refreshToken = config.sessionRecoveryRefreshToken ?? '';
                const authorization = String(config.headers.Authorization ?? '');
                const recovery = recoverySessions.get(refreshToken)?.tokenRecoveries.get(authorization);
                if (recovery) await waitForRecovery(recovery, recoverySignal as AbortSignal | undefined).catch((waitError: unknown) => {
                    if (recoverySignal?.aborted) throw waitError;
                });
                if (!recoverySignal?.aborted) endCurrentSession(deps, refreshToken, authorization);
            }
        }
        return Promise.reject(error);
    });
}

function endSession(
    session: RecoverySession,
    onSessionEnded: () => void,
): void {
    if (session.ended) return;
    session.ended = true;
    onSessionEnded();
}

async function postRefresh(refreshToken: string, signal?: AbortSignal): Promise<AuthResponse> {
    const response = await refreshClient.post(
        '/api/auth/refresh',
        toSnakeCase({ refreshToken }),
        { signal, timeout: refreshDeadlineMs },
    );
    return toCamelCase<AuthResponse>(response.data);
}

export function assertUploadSession(expectedRefreshToken: string | undefined, currentRefreshToken = getStoredAuth()?.refreshToken ?? ''): void {
    if (expectedRefreshToken !== undefined && expectedRefreshToken !== currentRefreshToken) throw new axios.CanceledError('Session changed');
}

export function attachAccessToken(
    instance: AxiosInstance,
    getAccessToken: () => string,
    getRefreshToken?: () => string,
): void {
    instance.interceptors.request.use((config) => {
        assertUploadSession(config.uploadSessionRefreshToken, getRefreshToken?.() ?? getStoredAuth()?.refreshToken ?? '');
        if (getRefreshToken && config.sessionRecoveryReplayed && config.sessionRecoveryRefreshToken !== getRefreshToken()) throw new axios.CanceledError('Session changed');
        const token = getAccessToken();
        config.headers.Authorization = token ? `Bearer ${token}` : '';
        return config;
    });
}

export function refreshSession(
    refreshToken: string,
    onSessionEnded: () => void,
    requestRefresh: RefreshRequest = postRefresh,
): Promise<AuthResponse> {
    const existing = refreshesInFlight.get(refreshToken);
    if (existing) return existing;
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), refreshDeadlineMs);
    const refresh = waitForRecovery(Promise.resolve()
        .then(() => requestRefresh(refreshToken, controller.signal)), controller.signal)
        .catch((error: unknown) => {
            onSessionEnded();
            throw error;
        })
        .finally(() => {
            clearTimeout(deadline);
            refreshesInFlight.delete(refreshToken);
        });
    refreshesInFlight.set(refreshToken, refresh);
    return refresh;
}

export function attachSessionRecovery(
    instance: AxiosInstance,
    deps: SessionRecoveryDeps,
): void {
    instance.interceptors.request.use((config) => {
        assertUploadSession(config.uploadSessionRefreshToken, deps.getRefreshToken());
        if (config.sessionRecoveryReplayed && config.sessionRecoveryRefreshToken !== deps.getRefreshToken()) throw new axios.CanceledError('Session changed');
        if (!config.sessionRecoveryReplayed) {
            config.sessionRecoveryRefreshToken = deps.getRefreshToken();
        }
        return config;
    });
    instance.interceptors.response.use(
        (response) => response,
        async (error: unknown) => {
            if (!axios.isAxiosError(error) || error.response?.status !== 401) {
                return Promise.reject(error);
            }
            const config = error.config;
            const recoverySignal = config?.sessionRecoverySignal ?? config?.signal;
            const refreshToken =
                config?.sessionRecoveryRefreshToken ?? deps.getRefreshToken();
            const failedAuthorizationHeader = String(config?.headers.Authorization ?? '');
            if (!config || recoverySignal?.aborted || deps.getRefreshToken() !== refreshToken) return Promise.reject(error);
            if (config.sessionRecoveryReplayed || !refreshToken) {
                const recovery = recoverySessions.get(refreshToken)?.tokenRecoveries.get(failedAuthorizationHeader);
                if (recovery) await waitForRecovery(recovery, recoverySignal as AbortSignal | undefined).catch((waitError: unknown) => {
                    if (recoverySignal?.aborted) throw waitError;
                });
                if (recoverySignal?.aborted) return Promise.reject(new axios.CanceledError());
                endCurrentSession(deps, refreshToken, failedAuthorizationHeader);
                return Promise.reject(error);
            }

            let accessToken = deps.getAccessToken?.();
            if (!accessToken || `Bearer ${accessToken}` === failedAuthorizationHeader) {
                try {
                    const auth = await refreshAuthSession({ ...deps, endOnRefreshFailure: true, signal: recoverySignal as AbortSignal | undefined }, refreshToken, failedAuthorizationHeader);
                    accessToken = deps.getAccessToken?.() ?? auth.accessToken;
                } catch (refreshError) {
                    return Promise.reject(recoverySignal?.aborted ? refreshError : error);
                }
            }
            if (recoverySignal?.aborted || deps.getRefreshToken() !== refreshToken) return Promise.reject(error);
            config.sessionRecoveryReplayed = true;
            config.headers.Authorization = `Bearer ${accessToken}`;
            // Auth rejects before the handler runs, so replaying even a POST is safe.
            return instance.request(config);
        },
    );
}

export function resetSessionRecovery(): void {
    refreshesInFlight.clear();
    recoverySessions.clear();
}
